/**
 * The slot vocabulary — the contract between PLACEMENT and STRUCTURE.
 *
 * Placement decides *what is here and why*; structure decides *where "here" is*.
 * Neither may know the other's business, and the slot is the only thing they
 * share. See `knowledge/projects/active/anchor-driven-surfaces/PRODUCT_PLAN.md`
 * and `surface-placement-spine/EXPERIENCE_MODEL_DECISIONS.md` §1.1.
 *
 * Two rules make that contract hold, and both come from measuring the anchors:
 *
 * 1. **A slot declares a ROLE, never a zone.** `FEATURE`, not "the right
 *    column". Structure decides that a feature lands in the column under HUB
 *    and in a full-width band under PAGE, and the same placement config then
 *    serves both — which is what stops a Drop configured for two channels from
 *    drifting into two configurations.
 *
 * 2. **A slot declares a FOOTPRINT IN BOTH STRUCTURES.** A widget that only
 *    works in one is unfinished. The PAGE form is not hypothetical: it already
 *    exists in every hub anchor as the ≤1180px rules, promoted here from a
 *    breakpoint to a mode.
 *
 * And one property that is easy to miss and expensive to omit:
 *
 * 3. **Every slot is OPTIONAL, and absence is a configuration.** Measured:
 *    `rttr-drop-hub.html` carries 164 of the publisher spine's 200 classes and
 *    DROPS 36 — the whole challenge rail (`chg__*`), the quest panel (`qp__*`),
 *    `mech--ad`, `fs--ad`, `lb__mine/mv/v`, `hs`, `xp`, `bar`, `hero__lock`. A
 *    structure that assumes its slots are populated cannot render that page.
 */

/**
 * WHAT a slot is for. Never where it goes.
 *
 * Deliberately small. These are the questions a surface answers, and the
 * anchors demonstrate all of them across six skins; a role that only one
 * surface would ever use is a widget, not a role.
 */
export enum SlotRole {
    /** The surface's headline occupant. Sizes to what is in it. One per surface. */
    STAGE = "STAGE",
    /** Section navigation for the surface. */
    NAV = "NAV",
    /**
     * The contested occupant — what the viewer is most likely to want beside
     * the stage. More than one may be eligible; see `OccupancyState`.
     */
    FEATURE = "FEATURE",
    /** A self-contained engagement that reads down rather than across. */
    ENGAGEMENT = "ENGAGEMENT",
    /** A scannable set of peers — cards the viewer moves along. */
    COLLECTION = "COLLECTION",
    /** A statement the full width of the surface. */
    STATEMENT = "STATEMENT",
}

/**
 * The SHAPE a slot occupies. Measured off the anchors, not chosen.
 *
 * Widths are the anchors' own: `.rail` 246px, `.side` 400px (340 when the stage
 * is expanded), the column widget 392px in `publisher-drop-hub.html` and 360 in
 * `rttr-drop-hub.html`, horizontal-rail children `flex:0 0 250px`.
 */
export enum SlotFootprint {
    /** Left float, 246px. Holds its width only while it exists. */
    RAIL = "RAIL",
    /** Right float, 400px, height `--colh`. The contested column. */
    COLUMN = "COLUMN",
    /**
     * Right float beside the rails, ~392px, `clear:right`, placed BEFORE the
     * rails in the DOM — a right float only has content to flow past it if that
     * content comes after it.
     */
    COLUMN_WIDGET = "COLUMN_WIDGET",
    /** Full width below the stage. */
    BAND = "BAND",
    /** Full width, children scroll horizontally with snap. */
    HORIZONTAL_RAIL = "HORIZONTAL_RAIL",
    /** The stage itself — `--band` tall, sizes to its occupant. */
    STAGE = "STAGE",
}

/**
 * What a footprint becomes under PAGE structure.
 *
 * This is not a design decision made here — it is the ≤1180px rule already
 * present in all seven hub anchors, read off them and promoted from a
 * breakpoint to a mode. Under PAGE nothing floats: the column's occupant and
 * every column widget become full-width blocks, and the rail becomes a
 * horizontal scroller.
 */
export const PAGE_FORM: Record<SlotFootprint, SlotFootprint> = {
    [SlotFootprint.RAIL]: SlotFootprint.HORIZONTAL_RAIL,
    [SlotFootprint.COLUMN]: SlotFootprint.BAND,
    [SlotFootprint.COLUMN_WIDGET]: SlotFootprint.BAND,
    [SlotFootprint.BAND]: SlotFootprint.BAND,
    [SlotFootprint.HORIZONTAL_RAIL]: SlotFootprint.HORIZONTAL_RAIL,
    [SlotFootprint.STAGE]: SlotFootprint.STAGE,
};

/**
 * A placed slot. `occupantId` is opaque here on purpose — structure positions
 * a box and never looks inside it.
 */
export interface SurfaceSlot {
    id: string;
    role: SlotRole;
    footprint: SlotFootprint;
    /** Global ordering when set; ties keep config order (stable sort). */
    order?: number;
    /**
     * Column arbitration rank — LOWER wins the contested column. Unset means
     * 100, which is where the featured default sits. This is the priority
     * `HubSpine.scss` documents ("standings > chat > featured"): a standings
     * board declares 0 and takes the column from the featured cards without
     * `resolveOccupancy` learning any occupant's type.
     */
    columnRank?: number;
    /**
     * Whether this slot currently has anything to show. A slot whose occupant
     * has ended is ABSENT, not empty — see `resolveOccupancy`, where a pin is
     * only a pin while its occupant exists.
     */
    present?: boolean;
    /**
     * Stage states in which this slot is a COLUMN CANDIDATE. Absent = all
     * states (every slot before this field existed). Declarative data, not a
     * render hook — it is how the anchor's Expanded-only chat becomes
     * expressible (Phase H gap register, defect 5): `stages: ["EXPANDED"]`
     * makes the room a candidate exactly while the stage is expanded, the
     * occupancy engine's candidate count crosses 1, and the column control
     * appears — Contract §3, drivable from config at last.
     * String literals rather than the StageState enum because slots.ts must
     * not import occupancy.ts (occupancy already imports slots).
     */
    stages?: readonly ("IDLE" | "WATCH" | "EXPANDED")[];
}

/** Slots that are actually renderable right now. Absence is a configuration. */
export function activeSlots(slots: readonly SurfaceSlot[]): SurfaceSlot[] {
    return slots
        .filter(s => s.present !== false)
        /*
         * `order` is GLOBAL when set; TIES KEEP CONFIG ORDER (Array.sort is
         * spec-stable). The old alphabetical id tie-break silently decided
         * section order by slot-id spelling whenever a config numbered each
         * footprint family from 1 — the obvious reading of "order within a
         * footprint" — and put three of RTTF's six sections in the wrong
         * place. Config order is the author's intent; spelling never is.
         */
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * The slots competing for the contested column, in preference order.
 *
 * `stage` gates candidacy through each slot's declared `stages`: a slot with
 * no declaration is a candidate in every state (the pre-field behavior), and
 * calling without a stage ignores the gate entirely — so nothing existing
 * changes until a config actually declares a stage-scoped slot.
 */
export function columnCandidates(slots: readonly SurfaceSlot[], stage?: string): SurfaceSlot[] {
    return activeSlots(slots).filter(s =>
        s.footprint === SlotFootprint.COLUMN &&
        (!s.stages || !stage || s.stages.includes(stage as "IDLE" | "WATCH" | "EXPANDED")))
        /* Rank arbitrates AUTO (candidates[0] wins); stable within a rank. */
        .sort((a, b) => (a.columnRank ?? 100) - (b.columnRank ?? 100));
}
